import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SidebarSingleContent,
  Box,
  constants,
  Center,
  BoxWithShadow,
  Stack,
  SolidDivider,
  ButtonWithAction,
  Cluster,
  util,
  Paragraph,
  Title,
} from '@thecb/components';
import AlertBar from '../../components/alert-bar';
import CartSidebar from '../../components/cart-sidebar';
import { CartItem } from './CartItem';
import {
  CancelOrderFormModal,
  RemoveItemModal,
  REMOVE_ITEM_MODAL,
} from './CartModals';
import { useDispatch, useSelector } from 'react-redux';
import {
  holdOrderRequest,
  submitCancelOrderRequest,
} from '../../pages/cart/Cart.state';
import SharedAlertBar from '../../components/alert-bar/SharedAlertBar';
const Header = ({ alertBar, alertBarActions }) => {
  const { alerts: sharedAlerts } = useSelector((state) => state.shared);
  return (
    <Box width="100%" padding="16px 0">
      <Center maxWidth="85.714rem">
        {alertBar.alerts.length > 0 && (
          <Box padding="16px 0">
            <AlertBar {...alertBar} {...alertBarActions} />
          </Box>
        )}
        {sharedAlerts.length > 0 && (
          <Box padding="16px 0">
            <SharedAlertBar />
          </Box>
        )}
        <Title
          id="cart-title"
          color={constants.colors.CHARADE_GREY}
          weight={constants.fontWeights.FONT_WEIGHT_BOLD}
        >
          Cart
        </Title>
      </Center>
    </Box>
  );
};

const Cart = ({
  forms,
  actions,
  alertBars,
  modals,
  order,
  getOrder,
  removeOrderItemRequest,
  displayAddItemsAlert,
}) => {
  const [errors, handleErrors] = useState(false);
  const [itemToRemove, setItemToRemove] = useState();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  useEffect(() => {
    if (!order.order_id) {
      getOrder();
    }
  }, []);
  const handleCancelOrderSubmit = util.general.generateClickHandler(
    forms.cancelOrderForm,
    () => handleErrors(true),
    () => dispatch(submitCancelOrderRequest({ id: order.order_id, navigate })),
  );

  const handleRemoveItemClick = (item_id) => {
    const item = order.order_items.find(
      (order_item) => order_item.id === item_id,
    );
    setItemToRemove(item);
    actions.modals.cartModal.openModal(REMOVE_ITEM_MODAL);
  };
  const handleRemoveItemSubmit = () => {
    removeOrderItemRequest(itemToRemove.id);
    actions.modals.cartModal.toggleModal();
  };

  const removeItemModal = (
    <RemoveItemModal
      modals={modals}
      submit={handleRemoveItemSubmit}
      toggle={actions.modals.cartModal.toggleModal}
      item={itemToRemove}
    />
  );
  const cancelOrderModal = (
    <CancelOrderFormModal
      {...forms.cancelOrderForm}
      {...actions.forms.cancelOrderForm}
      showErrors={errors}
      modals={modals}
      submit={handleCancelOrderSubmit}
      toggle={actions.modals.cartModal.toggleModal}
    />
  );

  const content = Boolean(order.order_items && order.order_items.length) ? (
    order.order_items.map((item, index) => (
      <CartItem
        remove={handleRemoveItemClick}
        key={`${item.receivable_category}-${index}`}
        id={item.id}
        name={item.receivable_category}
        subtotal={item.recorded_subtotal}
        total={item.recorded_price || item.recorded_subtotal}
        isNegativeAmount={item.is_negative_amount}
        externalAttributes={item.external_attributes_to_display}
      />
    ))
  ) : (
    <Paragraph>Your cart is empty</Paragraph>
  );
  const body = (
    <Box padding=".5rem 0 1.5rem">
      <BoxWithShadow
        background={constants.colors.WHITE}
        variant="baseStandard"
        padding="1.5rem"
        borderRadius="0.25rem"
      >
        {removeItemModal}
        {cancelOrderModal}
        <Box padding="0 0 1rem 0">
          <Cluster justify="flex-start" childGap="1rem">
            <ButtonWithAction
              text={'Cancel order'}
              isLoading={false}
              variant="secondary"
              action={() =>
                dispatch(
                  submitCancelOrderRequest({ id: order.order_id, navigate }),
                )
              }
              dataQa="Cancel order"
              textExtraStyles="font-size: 1rem;"
            />
            {(order.status !== 'incomplete' ||
              order?.transaction_details?.every(
                (detail) => detail.status === 'cancelled',
              )) && (
              <ButtonWithAction
                text={'Place on hold'}
                isLoading={false}
                variant="secondary"
                action={() => {
                  dispatch(holdOrderRequest({ navigate }));
                }}
                dataQa="Place on hold"
                textExtraStyles="font-size: 1rem;"
              />
            )}
          </Cluster>
        </Box>
        <SolidDivider />
        <Box padding="16px 0" id="cart-content">
          <Stack>{content}</Stack>
        </Box>
        <Box padding="16px 0">
          <Cluster justify="space-between">
            <ButtonWithAction
              extraStyles={'margin: 0;'}
              textExtraStyles="font-size: 1rem;"
              text={'Add more items'}
              isLoading={false}
              variant="secondary"
              action={() => {
                if (order.status !== 'incomplete') {
                  navigate('/pos/products');
                } else {
                  displayAddItemsAlert();
                }
              }}
              dataQa="Add more items"
            />
            <ButtonWithAction
              extraStyles={'margin: 0;'}
              text={'Checkout'}
              isLoading={false}
              variant="primary"
              action={() => navigate('/pos/checkout')}
              dataQa="Checkout"
              textExtraStyles="font-size: 1rem;"
            />
          </Cluster>
        </Box>
      </BoxWithShadow>
    </Box>
  );

  return (
    <SidebarSingleContent
      header={
        <Header
          key="CartHeader"
          alertBar={alertBars.cartAlertBars}
          alertBarActions={actions.alertBars.cartAlertBars}
        />
      }
      mainContent={body}
      sidebarContent={<CartSidebar />}
      sidebarTargetWidth="27.4289rem"
      sidebarWrapperMaxWidth="85.714rem"
      sidebarOnRight={true}
      childGap="0.5rem"
    />
  );
};

export default Cart;
